import { api } from './axios'
import { unref } from 'vue'
import type { AxiosRequestConfig, AxiosResponse } from 'axios'


const version = 'v1'

const konyveloApi = {
  // Teszteli, hogy a backend elérhető-e
  health() {
    return api.get(`/api/${version}/health`)
  },

  // Bejelentkezett felhasználó visszaadása
  self() {
    return api.get(`/api/${version}/self`, { skip401Redirect: true })
  },

  beallitasok_modositas(data: any) {
    return api.post(`/api/${version}/beallitasok`, data)
  },

  async bejelentkezes(data: any) {
    await api.get('/sanctum/csrf-cookie')
    return api.post(`/api/${version}/login`, data)
  },

  /** Szolgáltatói regisztráció */
  async regisztracio(data: any) {
    await api.get('/sanctum/csrf-cookie')
    return api.post(`/api/${version}/regisztracio`, data)
  },

  kijelentkezes() {
    return api.post(`/api/${version}/logout`)
  },

  /** más jelszavának módosítása */
  jelszó_módosít(id: number) {
    return api.post(`/api/${version}/felhasznalo/${id}/jelszo/visszaallit`);
  },

  /** más jelszavának módosítása (elfelejtett jelszó), de csak mélcím van meg */
  jelszó_módosít_maillel(mél: string) {
    return api.post(`/api/${version}/jelszo/visszaallit`, { mail: mél, });
  },

  // jelszó módosítása
  jelszo_modosit(id: number, expires: any, hash: any, signature: any, uj_jelszo: any, uj_jelszo_conf: any) {
    return api.post(`/api/${version}/jelszo/modosit/${id}?expires=${expires}&hash=${hash}&signature=${signature}`,
        {
            password: uj_jelszo,
            password_confirmation: uj_jelszo_conf,
        })
  },

  // új jelszó kérése
  jelszo_visszaallit(){
    return api.post(`/api/${version}/jelszo/visszaallit`)
  },

  // fájlok listázása
  fajlok_lista(page: number, per_page: number, sort_field: string, sort_order: number,
     filters: any, ugyfel_ceg_id: any, showSpinner = true, signal?: AbortSignal) {

    return api.get(`/api/${version}/fajlok`, {
      showSpinner,
      signal,
      params: {
        page: page,
        per_page: per_page,
        sort_field: sort_field,
        sort_order: sort_order,
        ...(ugyfel_ceg_id != null ? { ugyfel_ceg_id } : {}),
        ...unwrapFilters(filters),
      }
    })
  },

  szamla_adatok_modositasa(id: number, adatok: {
    elado: string | null
    vevo: string | null
    brutto_ertek: string | null
    penznem: string | null
    kiallitas_datuma: string | null
    teljesites_datuma: string | null
    fizetesi_hatarido: string | null
  }) {
    return api.patch(`/api/${version}/fajlok/${id}/szamla-adatok`, adatok)
  },

  szamla_kinyeres_ujrainditas(ugyfel_ceg_id: number, file_ids: number[]) {
    return api.post(`/api/${version}/fajlok/szamla-kinyeres/ujrainditas`, {
      ugyfel_ceg_id,
      file_ids,
      confirm_overwrite: true,
    })
  },

  // Letöltési dátum nélküli fájlok száma tabonként.
  fajlok_letoltetlen_darabszamok(ugyfel_ceg_id: number) {
    return api.get(`/api/${version}/fajlok/letoltetlen-darabszamok`, {
      params: { ugyfel_ceg_id },
      showSpinner: false,
    })
  },

  /**
   * Feltöltés utáni email értesítés küldése a megadott feltöltés azonosítók alapján.
   *
   * @param data A feltöltés azonosítók tömbje.
   * @returns Axios válasz.
   */
  lomtar_lista(ugyfel_ceg_id: any) {

    return api.get(`/api/${version}/fajlok/lomtar`, {
      params: {
        ...(ugyfel_ceg_id != null ? { ugyfel_ceg_id } : {}),
      }
    })
  },

  lomtar_visszaallitas(ids: number[]) {
    return api.post(`/api/${version}/fajlok/lomtar/tomeges_visszaallitas`, { ids })
  },

  lomtar_vegleges_torles(ids: number[]) {
    return api.post(`/api/${version}/fajlok/lomtar/tomeges_torles`, { ids })
  },

  lomtar_fajl_vegleges_torles(id: number) {
    return api.delete(`/api/${version}/fajlok/lomtar/${id}`)
  },

  fajlok_email_ertesites<T = any>(data: { feltoltes_idk: number[] }) {
    return api.post(`/api/${version}/fajlok/email-ertesites`, data)
  },

  /**
   * Fájlok feltöltése.
   *
   * @param data Feltöltendő FormData.
   * @param config Opcionális axios config (pl. onUploadProgress, signal).
   * @returns Axios válasz.
   */
  fajlok_feltolt<T = any>(data: FormData, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> {
    return api.post(`/api/${version}/fajlok`, data, {
      timeout: 120000,
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(config.headers ?? {}),
      },
    })
  },

  /**
   * Egy fájl letöltése.
   *
   * @param id A letöltendő fájl azonosítója.
   * @returns Blob válasz.
   */
  fajl_letolt(id: number) {
    return api.get(`/api/${version}/fajlok/${id}/fajl`, {
      responseType: 'blob',
    })
  },

  fajl_preview(id: number, config: AxiosRequestConfig = {}) {
    return api.get(`/api/${version}/fajlok/${id}/preview`, {
      ...config,
      responseType: 'blob',
    })
  },

  // data-ban felsorolt id-jú fájlok letöltése zipként
  fajl_tomeges_letolt(data: any){
    return api.post(`/api/${version}/fajlok/tomeges_letoltes`, data, {
      responseType: 'blob',
    })
  },

  felhasznalok_lista(params: any){
    return api.get(`/api/${version}/felhasznalo`, {params: params})
  },

  felhasznalo_ment(id: number, data: any) {
    if (!id || id == undefined){
      return api.post(`/api/${version}/felhasznalo`, data)
    }
    else {
      return api.patch(`/api/${version}/felhasznalo/${id}`, data)
    }
  },

  felhasznalo_torles(id: number){
    return api.delete(`/api/${version}/felhasznalo/${id}`)
  },

  /**
   * (csak admin számára) saját könyvelőiroda lekérdezése
   */
  sajat_konyveloceg(){
    return api.get(`/api/${version}/konyvelocegek/0`)
  },

  sajat_logo(){
    return api.get(`/api/${version}/konyvelocegek/0/logo`, {
      responseType: 'blob',
    })
  },

  /** Könyvelőcég mentése */
  konyveloceg_mentes(id: number, data: any){
    return api.put(`/api/${version}/konyvelocegek/${id}`, data)
  },

  /** Logok lekérdezése */
  log_lista(page: number, per_page: number, filters: any) {

    return api.get(`/api/${version}/logok`, {
      params: {
        page: page,
        per_page: per_page,
        ...unwrapFilters(filters),
      }
    })
  },

  cimke_szerkesztes(data: any){
    return api.post(`/api/${version}/fajlok/cimke-szerkesztes`, data)
  },


  ugyfelceg_ment(id: number, data: any){
    return api.put(`/api/${version}/ugyfelcegek/${id}`, data)
  },

  /** csoportos törlési kérelem. a törlendő a regiszterről vett lista, ami tömbként legalább az id és fajl_guid mezőértékekkel kell rendelkezzen */
  csoportos_törlés(törlendő: any) {
    return api./*delete*/post(`/api/${version}/fajlok/tomeges_torles`, { data: törlendő, });
  },

  // egy fájl frissítése
  fajl_frissit(id: number, data: any) {
    return api.patch(`/api/${version}/fajlok/${id}`, data)
  },

  ugyfelcegek_lista() {
    return api.get(`/api/${version}/ugyfelcegek`)
  },

  ugyfel_aktiv(id: number, data: any){
    return api.patch(`/api/${version}/ugyfelek/${id}/aktiv`, data)
  },

  konyvelo_aktiv(id: number, data: any){
    return api.patch(`/api/${version}/konyvelok/${id}/aktiv`, data)
  },

  konyvelo_ugyfelek(id: number){
    return api.get(`/api/${version}/konyvelo-ugyfelek`, {params: {konyvelo_id: id, aktiv: true}})
  },

  kategoriak_lista(data: any){
    return api.get(`/api/${version}/kategoriak`, {params: data})
  },

  kategoria_ment(id: number, data: any){
    return api.post(`/api/${version}/kategoriak/${id}`, data)
  },

  kategoria_torol(id: number){
    return api.delete(`/api/${version}/kategoriak/${id}`)
  },

  uzenet_elkuld(ugyfel_id: number, data: any){
    return api.post(`/api/${version}/ugyfelek/${ugyfel_id}/nyugta`, data)
  },

  // --- Előfizetés ---

  /** Jelenlegi előfizetési adatok lekérdezése */
  elofizetes_jelenlegi() {
    return api.get(`/api/${version}/elofizetes`)
  },

  /** Publikus előfizetési csomagok listája */
  elofizetesi_tipusok() {
    return api.get(`/api/${version}/elofizetesi-tipusok`)
  },

  elofizetesi_tipusok_osszes() {
    return api.get(`/api/${version}/elofizetesi-tipusok/osszes`)
  },

  /** Új előfizetés indítása (Stripe Checkout) */
  elofizet(data: { elofizetesi_tipus_id: number; idoszak: 'havi' | 'eves' }) {
    return api.post(`/api/${version}/elofizetes`, data)
  },

  /** Meglévő fizetős előfizetés módosítása (upgrade / downgrade) */
  csomag_valtas(data: { elofizetesi_tipus_id: number; idoszak: 'havi' | 'eves' }) {
    return api.put(`/api/${version}/elofizetes/csomag_valtas`, data)
  },

  /** Aktív Stripe előfizetés lemondása (az időszak végén szűnik meg) */
  elofizetes_lemond() {
    return api.delete(`/api/${version}/elofizetes`)
  },

  /** Lemondott, de még futó előfizetés újra aktiválása (cancel_at_period_end törlése) */
  elofizetes_ujraaktival() {
    return api.post(`/api/${version}/elofizetes/ujraaktival`)
  },

  /** Stripe Customer Portal session URL lekérdezése kártyamódosításhoz */
  elofizetes_portal() {
    return api.post(`/api/${version}/elofizetes/portal`)
  },

  /** Fizetési mód (pm_type, pm_last_four) frissítése Stripe-ból visszatérés után */
  elofizetes_pm_frissites() {
    return api.post(`/api/${version}/elofizetes/pm_frissites`)
  },

  elofizetesek() {
    return api.get(`/api/${version}/konyvelocegek/elofizetesek`)
  }

}

// A célja, hogy a FajlokView.vue-ban található filter objektumból kicsomagolja
// a valós szűrő értékeket, amelyeket a backendnek kell küldeni
function unwrapFilters(filters: any) {
  return Object.fromEntries(
    Object.entries(filters).map(([k, v]) => {
      let val: any = unref(v)
      // if (val instanceof Date) {
      //   // format as UTC ISO string
      //   val = val.toISOString()
      // }

      if (val && typeof val === "object" && "value" in val) {
        val = val.label
      }

      // if (Array.isArray(val) && val.length === 0) {
      //   val = undefined
      // }

      return [k, val ?? undefined]
    })
  )
}

export default konyveloApi
